/**
 * AdminTour-specific types and step definitions.
 *
 * The AdminTour is the guided product walkthrough that runs during onboarding
 * (when currentStep is 'demo') and can be replayed from the Help page.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each AdminTour step */
export type AdminTourStepId =
  | 'welcome'
  | 'conversations'
  | 'assigned_to_me'
  | 'assign_btn'
  | 'unassigned'
  | 'contacts_nav'
  | 'contacts_page'
  | 'bulk_message'
  | 'add_contact'
  | 'teams_nav'
  | 'teams_new_chat'
  | 'teams_conversations'
  | 'settings_nav'
  | 'settings_tabs'
  | 'agency_mgmt_nav'
  | 'add_member_btn'
  | 'add_member_form'
  | 'team_invitations'
  | 'notifications_nav'
  | 'notifications_page'
  | 'help_nav'
  | 'help_page'
  | 'tour_complete';

/** Ordered list of all AdminTour steps */
export const ADMIN_TOUR_STEPS: TourStep[] = [
  { id: 'welcome', type: 'modal', title: '', content: '', navigateTo: '/dashboard?view=all' },
  {
    id: 'conversations',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-all"]',
    title: 'All Conversations',
    content:
      'This tab shows all conversations happening inside and outside your agency. Every conversation is visible to your entire team, keeping communication transparent and traceable while enabling seamless collaboration so no client inquiry goes unnoticed.',
    placement: 'right',
  },
  {
    id: 'assigned_to_me',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-assigned"]',
    title: 'Assigned to Me',
    content:
      "This tab filters the conversation list to show all conversations assigned to you. This way, you can stay on top of what you're responsible for.",
    placement: 'right',
  },
  {
    id: 'assign_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="assign-btn"]',
    title: 'How to Assign',
    content:
      'This button shows a dropdown of everybody in your agency — including yourself at the top. Select any agent to assign the conversation to them so it appears in their "Assigned to Me" list.',
    placement: 'bottom',
  },
  {
    id: 'unassigned',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-unassigned"]',
    title: 'Unassigned',
    content:
      'This tab filters the list to show unassigned conversations, ensuring no client inquiry or potential lead slips through the cracks.',
    placement: 'right',
  },
  {
    id: 'contacts_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="contacts"]',
    title: 'Contacts',
    content: 'Click here to view and manage all your contacts.',
    placement: 'right',
    advanceOnNavigate: '/contacts',
  },
  {
    id: 'contacts_page',
    type: 'highlight',
    targetSelector: '[data-tour-id="contacts-page"]',
    title: 'Contact Page',
    content:
      'This is your contact page. All AMS client data will automatically upload here, creating contacts for you.\n\nAny edit or update that ever happens inside your AMS will automatically update here in Voley, saving you hundreds of hours of manual data entry.',
    placement: 'top',
    positionInside: true,
  },
  {
    id: 'bulk_message',
    type: 'highlight',
    targetSelector: '[data-tour-id="bulk-message-btn"]',
    title: 'Bulk Messaging',
    content:
      'After selecting multiple contacts, hit this to send one mass message to every contact. This doesn\u2019t put them in one group message, it\u2019s all individual. That way, it feels personal to the customer, but you get hours of work in one click!',
    placement: 'bottom',
  },
  {
    id: 'add_contact',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-contact-btn"]',
    title: 'Add Contact',
    content:
      "You can create contacts manually here. However, any client detail updates made inside Voley won't sync back to your AMS client profiles. We keep your AMS as the single source of truth for client information.",
    placement: 'left',
  },
  {
    id: 'teams_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="teams"]',
    title: 'Teams',
    content: 'Click here to access your internal team communication.',
    placement: 'right',
    advanceOnNavigate: '/teams',
  },
  {
    id: 'teams_new_chat',
    type: 'highlight',
    targetSelector: '[data-tour-id="teams-add-channel-btn"]',
    title: 'Create a Group',
    content: 'Create public or private chats to message groups of your agents.',
    placement: 'right',
  },
  {
    id: 'teams_conversations',
    type: 'highlight',
    targetSelector: '[data-tour-id="teams-conversations-list"]',
    title: 'Direct Messages',
    content: 'Or directly message agents once you add them.',
    placement: 'right',
  },
  {
    id: 'settings_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings"]',
    title: 'Settings',
    content: 'Click here to customize your agency and personal settings.',
    placement: 'right',
    advanceOnNavigate: '/settings',
  },
  {
    id: 'settings_tabs',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings-tabs"]',
    title: 'Settings',
    content:
      'Navigate through settings to customize your agency and personal info, as well as other agency-specific tools you would like.',
    placement: 'right',
  },
  {
    id: 'agency_mgmt_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="settings-nav-agency"]',
    title: 'Agency Management',
    content: 'Click here to manage your agency\u2019s team and settings.',
    placement: 'right',
    advanceOnNavigate: '/settings?section=agency',
  },
  {
    id: 'add_member_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-member-btn"]',
    title: 'Add a Team Member',
    content: 'Click here to invite a new team member to your agency.',
    placement: 'left',
    advanceOnElement: '[data-tour-id="add-member-form"]',
  },
  {
    id: 'add_member_form',
    type: 'highlight',
    targetSelector: '[data-tour-id="add-member-form"]',
    title: 'Invite a Team Member',
    content:
      'Fill in their first name, last name, and email, then click Send Invitation. They\u2019ll receive an email invite to join your agency on Voley.',
    placement: 'top',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="add-member-form"]',
  },
  {
    id: 'team_invitations',
    type: 'highlight',
    targetSelector: '[data-tour-id="team-invitations"]',
    title: 'Pending Invitations & Team Members',
    content:
      'This is where you\u2019ll see all pending invitations you\u2019ve sent. Once a team member accepts and completes their setup, they\u2019ll move from here to the Team Members list above. You can also resend or cancel invitations from this section.',
    placement: 'left',
  },
  {
    id: 'notifications_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="notifications"]',
    title: 'Notifications',
    content: 'Click here to manage your notification preferences.',
    placement: 'right',
    advanceOnNavigate: '/notifications',
  },
  {
    id: 'notifications_page',
    type: 'highlight',
    targetSelector: '[data-tour-id="notifications-page"]',
    title: 'Notification Settings',
    content:
      'Enable in-app and push notifications for different actions to have complete real-time visibility of what is happening across your agency and your conversations.',
    placement: 'top',
    positionInside: true,
  },
  {
    id: 'help_nav',
    type: 'highlight',
    targetSelector: '[data-tour-id="help"]',
    title: 'Help & Training',
    content: 'Click here to access guided tours, documentation, and training for your team.',
    placement: 'right',
    advanceOnNavigate: '/help',
  },
  {
    id: 'help_page',
    type: 'highlight',
    targetSelector: '[data-tour-id="help-page"]',
    title: 'Training & Documentation',
    content:
      'Voley is built with purpose-driven simplicity so there\u2019s almost no overhead. Use these guided tours to train your entire team quickly and easily \u2014 each one walks through a feature step by step, right inside the app.\n\nYou\u2019ll also find documentation and quick-start guides here if you ever need a reference.',
    placement: 'top',
    positionInside: true,
  },
  {
    id: 'tour_complete',
    type: 'modal',
    title: 'You\u2019re All Set!',
    content:
      'Your agency is ready to go. Start messaging your contacts, manage conversations, and grow your business with Voley.',
    navigateTo: '/dashboard?view=all',
  },
];

/** Hotspot IDs required by the backend for demo step validation */
export const REQUIRED_HOTSPOTS = [
  'conversations',
  'assigned_to_me',
  'assign_btn',
  'unassigned',
  'contacts_nav',
  'bulk_message',
  'add_contact',
  'teams_nav',
  'teams_new_chat',
  'teams_conversations',
  'settings_nav',
  'settings_tabs',
  'agency_mgmt_nav',
  'add_member_btn',
  'add_member_form',
  'team_invitations',
  'notifications_nav',
  'notifications_page',
  'help_nav',
  'help_page',
  'tour_complete',
] as const;
